import { useEffect, useId, useRef, useState } from 'react';
import { useAuditCatalogue } from '../../context/AuditCatalogueContext';

interface ActionSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Grouped multi-select (Audit screen). Single native select otherwise (admin-user tab). */
  multiple?: boolean;
  /** Visible label text; also the accessible name. */
  label?: string;
  id?: string;
}

const VISIBLE_CHIPS = 3;

/**
 * Spec 069 AC-C4 — the action picker. Its options are the catalogue (grouped by entity group), so the
 * admin-user tab and the Audit screen share one vocabulary and no ghost codes.
 */
export default function ActionSelect({ value, onChange, multiple = false, label = 'Action', id }: ActionSelectProps) {
  const { groupedOptions, labelOf, loading, error } = useAuditCatalogue();
  const autoId = useId();
  const selectId = id ?? `action-select-${autoId}`;
  const [open, setOpen] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!multiple) {
    return (
      <div className="audit-field">
        <label htmlFor={selectId} className="audit-field__label">{label}</label>
        <select
          id={selectId}
          className="admin-select"
          value={value[0] ?? ''}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
          disabled={loading && groupedOptions.length === 0}
        >
          <option value="">{error ? 'All actions (list unavailable)' : 'All actions'}</option>
          {groupedOptions.map((g) => (
            <optgroup key={g.code} label={g.label}>
              {g.actions.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
            </optgroup>
          ))}
          {/* A code in the URL that the catalogue no longer has stays selectable, so the filter is visible. */}
          {value[0] && !groupedOptions.some((g) => g.actions.some((a) => a.code === value[0])) && (
            <option value={value[0]}>{labelOf(value[0])}</option>
          )}
        </select>
      </div>
    );
  }

  const toggle = (code: string) => {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  };
  const chips = showAllChips ? value : value.slice(0, VISIBLE_CHIPS);
  const hidden = value.length - chips.length;

  return (
    <div className="audit-field audit-action-select" ref={rootRef}>
      <span className="audit-field__label" id={`${selectId}-label`}>{label}</span>
      <button
        type="button"
        id={selectId}
        className="admin-select audit-action-select__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-labelledby={`${selectId}-label ${selectId}`}
        onClick={() => setOpen((o) => !o)}
      >
        {value.length === 0 ? 'All actions' : `${value.length} selected`}
      </button>
      {open && (
        <div className="audit-action-select__panel" role="group" aria-label={`${label} options`}>
          {error && <p className="audit-hint audit-hint--error">The action list could not load. {error}</p>}
          {loading && groupedOptions.length === 0 && <p className="audit-hint">Loading actions…</p>}
          {groupedOptions.map((g) => (
            <fieldset key={g.code} className="audit-action-select__group">
              <legend>{g.label}</legend>
              {g.actions.map((a) => (
                <label key={a.code} className="audit-action-select__option">
                  <input type="checkbox" checked={value.includes(a.code)} onChange={() => toggle(a.code)} />
                  <span>{a.label}</span>
                </label>
              ))}
            </fieldset>
          ))}
          <div className="audit-action-select__footer">
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => onChange([])} disabled={value.length === 0}>
              Clear
            </button>
            <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
      {value.length > 0 && (
        <div className="audit-chips">
          {chips.map((code) => (
            <span key={code} className="audit-chip">
              {labelOf(code)}
              <button type="button" className="audit-chip__remove" aria-label={`Remove ${labelOf(code)}`} onClick={() => toggle(code)}>
                ×
              </button>
            </span>
          ))}
          {(hidden > 0 || showAllChips) && value.length > VISIBLE_CHIPS && (
            <button
              type="button"
              className="audit-chip audit-chip--more"
              aria-expanded={showAllChips}
              onClick={() => setShowAllChips((s) => !s)}
            >
              {showAllChips ? 'Show less' : `+${hidden}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
